import { getEntry, listCollection } from "../lib/content.server.js";
import { articleJsonLd, breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { related } from "../lib/related.js";
import PageHero from "../components/PageHero/PageHero.jsx";
import Prose from "../components/Prose/Prose.jsx";
import VideoEmbed from "../components/VideoEmbed/VideoEmbed.jsx";
import EntryCard from "../components/EntryCard/EntryCard.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import styles from "./case-study.module.css";

const PRODUCT_LABEL = {
  rsa3d: "Road Safety Audit 3D",
  "123bim": "3D Visualization",
  both: "RSA 3D and 3D Visualization",
};

export async function loader({ params }) {
  const entry = getEntry("projects", params.slug);
  if (!entry) throw new Response("Not Found", { status: 404 });
  const more = related(listCollection("projects"), entry, "product", 3);
  return { entry, more };
}

export function meta({ loaderData }) {
  if (!loaderData)
    return buildMeta({
      title: "Project not found",
      description: "This project does not exist.",
      path: "/projects",
      noindex: true,
    });
  const { entry } = loaderData;
  const path = `/projects/${entry.slug}`;
  return buildMeta({
    title: entry.title,
    description: entry.description,
    path,
    image: entry.image,
    type: "article",
    jsonLd: [
      articleJsonLd(entry, path),
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Projects", path: "/projects" },
        { name: entry.title, path },
      ]),
    ],
  });
}

export default function Project({ loaderData }) {
  const { entry, more } = loaderData;
  return (
    <>
      <PageHero
        eyebrow="Project"
        title={entry.title}
        lead={entry.description}
        image={entry.image}
        imageAlt={entry.imageAlt}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Projects", path: "/projects" },
          { name: entry.title, path: `/projects/${entry.slug}` },
        ]}
      >
        <dl className={styles.summary}>
          <div>
            <dt className="eyebrow">Client</dt>
            <dd>{entry.client}</dd>
          </div>
          {entry.location ? (
            <div>
              <dt className="eyebrow">Location</dt>
              <dd>{entry.location}</dd>
            </div>
          ) : null}
          {entry.year ? (
            <div>
              <dt className="eyebrow">Year</dt>
              <dd>{entry.year}</dd>
            </div>
          ) : null}
          <div>
            <dt className="eyebrow">Product</dt>
            <dd>{PRODUCT_LABEL[entry.product]}</dd>
          </div>
          {entry.services && entry.services.length ? (
            <div>
              <dt className="eyebrow">Services</dt>
              <dd>{entry.services.join(", ")}</dd>
            </div>
          ) : null}
        </dl>
      </PageHero>
      <section className="section">
        <div className="container">
          <Prose html={entry.html} />
          {entry.video ? (
            <div className={styles.video}>
              <VideoEmbed video={entry.video} title={entry.title} poster={entry.image} />
            </div>
          ) : null}
        </div>
      </section>
      {more.length ? (
        <section className="section section-alt">
          <div className="container">
            <SectionHeader eyebrow="More" title="Related projects" />
            <div className="grid grid-3">
              {more.map((p) => (
                <EntryCard
                  key={p.slug}
                  kind="project"
                  href={`/projects/${p.slug}`}
                  image={p.image}
                  imageAlt={p.imageAlt}
                  title={p.title}
                  description={p.description}
                  meta={p.client}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}
      <CTABand title="Visualize your project" />
    </>
  );
}
